import { makeAutoObservable, runInAction } from "mobx";
import {
  listPrograms,
  getProgram,
  getProject,
  listProjectsForProgram,
  listRecentChats,
  listProjectMembers,
  listChatsForProject,
  pinArtifactToProject,
  listPinnedArtifacts,
  unpinArtifactFromProject,
  shareArtifact,
  createProject as createProjectQuery,
  createProgram as createProgramQuery,
  updateProgram as updateProgramQuery,
  deleteProgram as deleteProgramQuery,
  updateProject as updateProjectQuery,
  deleteProject as deleteProjectQuery,
} from "../queries/workspace";

const projectKey = (programId, projectId) => `${programId}/${projectId}`;

export class WorkspaceStore {
  programs = [];
  programsLoading = false;
  programsError = null;
  programNotEntitled = {};

  projectsByProgram = {};
  projectsLoading = {};
  projectsError = {};
  // PLEX-6756: projects the caller reaches by a project-only grant, keyed by
  // projectId -> "allowed" | "denied", and the granted project records by
  // program. Kept apart from projectsByProgram because loadProjects replaces
  // that list with the caller's own partition, which never holds a grant.
  projectAccess = {};
  grantedProjectsByProgram = {};

  recentChats = [];
  recentChatsLoading = false;
  recentChatsError = null;

  membersByProject = {};
  membersError = {};
  membersNotEntitled = {};
  chatsByProject = {};
  pinnedByProject = {};
  shareUrlByArtifact = {};

  // Promise caches keyed by the relevant id. While a load is in flight, any
  // second caller (e.g. a parent route's useEffect plus a child route's
  // useEffect) gets the same promise instead of firing a duplicate request.
  // Cleared in .finally() once the request settles.
  _inflightPrograms = null;
  _inflightProjects = new Map();
  _inflightChats = new Map();
  _inflightMembers = new Map();

  constructor(userStore) {
    this.userStore = userStore;
    makeAutoObservable(this, {
      _inflightPrograms: false,
      _inflightProjects: false,
      _inflightChats: false,
      _inflightMembers: false,
    });
  }

  get authData() {
    return this.userStore?.authData || null;
  }

  loadPrograms = async () => {
    if (this._inflightPrograms) return this._inflightPrograms;
    this.programsLoading = true;
    this.programsError = null;
    const promise = (async () => {
      try {
        const programs = await listPrograms(this.authData);
        runInAction(() => {
          this.programs = programs;
          this.programsLoading = false;
        });
        return programs;
      } catch (e) {
        runInAction(() => {
          this.programsError = e?.message || String(e);
          this.programsLoading = false;
        });
        throw e;
      }
    })().finally(() => {
      this._inflightPrograms = null;
    });
    this._inflightPrograms = promise;
    return promise;
  };

  getProgramById = (programId) =>
    this.programs.find((p) => p.id === programId) || null;

  loadProgram = async (programId) => {
    // The denied flag is NOT cleared up-front. Clearing it before the request
    // settles briefly re-renders the shell for a user who is already known to
    // be denied, and any caller that re-checks turns that into a visible
    // flicker (PR #18 review). It is cleared on success instead, so a genuine
    // grant still promotes the user out of the denied state.
    try {
      const program = await getProgram(this.authData, programId);
      runInAction(() => {
        this.programNotEntitled[programId] = false;
        const idx = this.programs.findIndex((p) => p.id === programId);
        if (idx >= 0) {
          this.programs[idx] = program;
        } else {
          this.programs.push(program);
        }
      });
      return program;
    } catch (e) {
      // PLEX-4122: a not-entitled (403) failure is tracked separately so
      // AccessGate can render the permission-denied screen instead of a
      // silent-blank shell — mirrors membersNotEntitled / loadMembersForProject.
      runInAction(() => {
        if (e?.notEntitled) {
          this.programNotEntitled[programId] = true;
        }
      });
      throw e;
    }
  };

  getProgramNotEntitled = (programId) => !!this.programNotEntitled[programId];

  /**
   * PLEX-6756: the project check AccessGate runs once the program GET has
   * refused the caller. A project-only grant (PLEX-4125) reaches the project
   * but not its program, since access inherits downward only, so the program
   * refusal alone can't deny the project. Any failure here denies: the program
   * already said no, and this is the only other way in.
   *
   * As in loadProgram, the recorded state is not cleared up-front, so a re-check
   * never flashes the other screen while it is in flight.
   */
  checkProjectAccess = async (programId, projectId) => {
    try {
      const project = await getProject(this.authData, projectId);
      runInAction(() => {
        this.projectAccess[projectId] = "allowed";
        const others = (this.grantedProjectsByProgram[programId] || []).filter((p) => p.id !== projectId);
        this.grantedProjectsByProgram[programId] = [...others, { ...project, shared: true }];
      });
      return project;
    } catch (e) {
      runInAction(() => {
        this.projectAccess[projectId] = "denied";
      });
      throw e;
    }
  };

  getProjectAccess = (projectId) => this.projectAccess[projectId] || null;

  loadProjects = async (programId) => {
    const existing = this._inflightProjects.get(programId);
    if (existing) return existing;
    runInAction(() => {
      this.projectsLoading[programId] = true;
      this.projectsError[programId] = null;
    });
    const promise = (async () => {
      try {
        const projects = await listProjectsForProgram(this.authData, programId);
        runInAction(() => {
          this.projectsByProgram[programId] = projects;
          this.projectsLoading[programId] = false;
        });
        return projects;
      } catch (e) {
        runInAction(() => {
          this.projectsError[programId] = e?.message || String(e);
          this.projectsLoading[programId] = false;
        });
        throw e;
      }
    })().finally(() => {
      this._inflightProjects.delete(programId);
    });
    this._inflightProjects.set(programId, promise);
    return promise;
  };

  getProjectsForProgram = (programId) => {
    const own = this.projectsByProgram[programId] || [];
    const granted = (this.grantedProjectsByProgram[programId] || []).filter(
      (g) => !own.some((p) => p.id === g.id),
    );
    return granted.length ? [...own, ...granted] : own;
  };

  loadRecentChats = async ({ programId } = {}) => {
    this.recentChatsLoading = true;
    this.recentChatsError = null;
    try {
      const chats = await listRecentChats(this.authData, { programId });
      runInAction(() => {
        this.recentChats = chats;
        this.recentChatsLoading = false;
      });
      return chats;
    } catch (e) {
      runInAction(() => {
        this.recentChatsError = e?.message || String(e);
        this.recentChatsLoading = false;
      });
      throw e;
    }
  };

  loadMembersForProject = async (programId, projectId) => {
    const key = projectKey(programId, projectId);
    const existing = this._inflightMembers.get(key);
    if (existing) return existing;
    runInAction(() => {
      this.membersError[key] = null;
      this.membersNotEntitled[key] = false;
    });
    const promise = (async () => {
      try {
        const members = await listProjectMembers(this.authData, programId, projectId);
        runInAction(() => {
          this.membersByProject[key] = members;
        });
        return members;
      } catch (e) {
        // A not-entitled failure is tracked separately from a generic error
        // so TeamDrawer can show "you don't have access" instead of "no
        // members at this scope" — those used to be indistinguishable
        // because fetchOrFixture silently returned empty fixture data for
        // a 403, same as it does for "not shipped on this cell yet" (404).
        runInAction(() => {
          if (e?.notEntitled) {
            this.membersNotEntitled[key] = true;
            this.membersError[key] = null;
          } else {
            this.membersError[key] = e?.message || String(e);
          }
        });
        // Not-entitled is an expected, fully-handled state: TeamDrawer renders an
        // access-denied panel from membersNotEntitled. Resolve to an empty list
        // rather than rethrowing — the caller (ProjectHeader) fires this
        // fire-and-forget, so a rejected NotEntitledError would escape as an
        // unhandled promise rejection and break the UI (PLEX-4221 / Sentry
        // WEBAPP-79). Genuine errors still propagate for callers to surface.
        if (e?.notEntitled) return [];
        throw e;
      }
    })().finally(() => {
      this._inflightMembers.delete(key);
    });
    this._inflightMembers.set(key, promise);
    return promise;
  };

  getMembersForProject = (programId, projectId) =>
    this.membersByProject[projectKey(programId, projectId)] || [];

  getMembersErrorForProject = (programId, projectId) =>
    this.membersError[projectKey(programId, projectId)] || null;

  getMembersNotEntitledForProject = (programId, projectId) =>
    !!this.membersNotEntitled[projectKey(programId, projectId)];

  loadChatsForProject = async (programId, projectId) => {
    const key = projectKey(programId, projectId);
    const existing = this._inflightChats.get(key);
    if (existing) return existing;
    const promise = (async () => {
      const chats = await listChatsForProject(this.authData, programId, projectId);
      runInAction(() => {
        this.chatsByProject[key] = chats;
      });
      return chats;
    })().finally(() => {
      this._inflightChats.delete(key);
    });
    this._inflightChats.set(key, promise);
    return promise;
  };

  getChatsForProject = (programId, projectId) =>
    this.chatsByProject[projectKey(programId, projectId)] || [];

  addLocalChatForProject = (programId, projectId, chat) => {
    const key = projectKey(programId, projectId);
    const current = this.chatsByProject[key] || [];
    this.chatsByProject[key] = [chat, ...current];
  };

  getPinnedArtifacts = (programId, projectId) =>
    this.pinnedByProject[projectKey(programId, projectId)] || [];

  pinArtifact = async (programId, projectId, artifact) => {
    const result = await pinArtifactToProject(this.authData, programId, projectId, artifact);
    runInAction(() => {
      const key = projectKey(programId, projectId);
      const current = this.pinnedByProject[key] || [];
      this.pinnedByProject[key] = [
        { ...artifact, pinnedAt: result.pinnedAt },
        ...current.filter((a) => a.artifactId !== artifact.artifactId),
      ];
    });
    return result;
  };

  // Remove a pin (unpin) — drops it locally and soft-deletes it server-side.
  unpinArtifact = async (programId, projectId, artifactId) => {
    const key = projectKey(programId, projectId);
    const prev = this.pinnedByProject[key] || [];
    runInAction(() => { this.pinnedByProject[key] = prev.filter((a) => a.artifactId !== artifactId); });
    try {
      await unpinArtifactFromProject(this.authData, programId, projectId, artifactId);
    } catch (e) {
      runInAction(() => { this.pinnedByProject[key] = prev; }); // restore on failure
      throw e;
    }
  };

  // Load persisted pins so they show across sessions (not just session-pinned).
  loadPinnedArtifacts = async (programId, projectId) => {
    if (!programId || !projectId) return;
    try {
      const resp = await listPinnedArtifacts(this.authData, programId, projectId);
      runInAction(() => {
        this.pinnedByProject[projectKey(programId, projectId)] = resp?.artifacts || [];
      });
    } catch (_) {
      /* non-fatal — drawer just won't show persisted pins */
    }
  };

  createProject = async (programId, input) => {
    const project = await createProjectQuery(this.authData, programId, input);
    runInAction(() => {
      const current = this.projectsByProgram[programId] || [];
      this.projectsByProgram[programId] = [project, ...current];
      // Bump program-level project count if loaded
      const programIdx = this.programs.findIndex((p) => p.id === programId);
      if (programIdx >= 0) {
        this.programs[programIdx] = {
          ...this.programs[programIdx],
          projectCount: (this.programs[programIdx].projectCount || 0) + 1,
        };
      }
    });
    return project;
  };

  /**
   * Create a top-level program. Backend POST /api/programs accepts
   * { name, description?, id? } and returns the persisted program.
   * The new program is prepended to the in-memory list so the workspace
   * home reflects it immediately without a refetch.
   */
  createProgram = async (input) => {
    const program = await createProgramQuery(this.authData, input);
    runInAction(() => {
      this.programs = [program, ...this.programs.filter((p) => p.id !== program.id)];
    });
    return program;
  };

  /**
   * Update a program's name and/or description. Optimistic — patches
   * the in-memory record before awaiting the network so the UI feels
   * instant. On failure, the next loadPrograms() resync rectifies.
   */
  updateProgram = async (programId, patch) => {
    const idx = this.programs.findIndex((p) => p.id === programId);
    const prev = idx >= 0 ? { ...this.programs[idx] } : null;
    if (idx >= 0) {
      runInAction(() => {
        this.programs[idx] = { ...this.programs[idx], ...patch };
      });
    }
    try {
      const updated = await updateProgramQuery(this.authData, programId, patch);
      if (updated) {
        runInAction(() => {
          const i = this.programs.findIndex((p) => p.id === programId);
          if (i >= 0) this.programs[i] = updated;
        });
      }
      return updated;
    } catch (e) {
      if (prev) runInAction(() => { this.programs[idx] = prev; });
      throw e;
    }
  };

  /**
   * Soft-delete a program. Optimistic — removes from the in-memory
   * list immediately; rolls back on failure.
   */
  deleteProgram = async (programId) => {
    const idx = this.programs.findIndex((p) => p.id === programId);
    const prev = idx >= 0 ? this.programs[idx] : null;
    if (idx >= 0) {
      runInAction(() => {
        this.programs = this.programs.filter((p) => p.id !== programId);
        delete this.projectsByProgram[programId];
      });
    }
    try {
      await deleteProgramQuery(this.authData, programId);
      return true;
    } catch (e) {
      if (prev) runInAction(() => { this.programs = [prev, ...this.programs]; });
      throw e;
    }
  };

  /**
   * Update a project. Optimistic patch on projectsByProgram; rolls
   * back on failure. Backend validator requires programId in the
   * payload, so callers MUST pass it.
   */
  updateProject = async (programId, projectId, patch) => {
    const list = this.projectsByProgram[programId] || [];
    const idx = list.findIndex((p) => p.id === projectId);
    const prev = idx >= 0 ? { ...list[idx] } : null;
    if (idx >= 0) {
      runInAction(() => {
        this.projectsByProgram[programId] = [
          ...list.slice(0, idx),
          { ...list[idx], ...patch },
          ...list.slice(idx + 1),
        ];
      });
    }
    try {
      const merged = { ...(prev || {}), ...patch, programId };
      const updated = await updateProjectQuery(this.authData, projectId, merged);
      if (updated) {
        runInAction(() => {
          const i = (this.projectsByProgram[programId] || []).findIndex((p) => p.id === projectId);
          if (i >= 0) this.projectsByProgram[programId][i] = { ...this.projectsByProgram[programId][i], ...updated };
        });
      }
      return updated;
    } catch (e) {
      if (prev) {
        runInAction(() => {
          const i = (this.projectsByProgram[programId] || []).findIndex((p) => p.id === projectId);
          if (i >= 0) this.projectsByProgram[programId][i] = prev;
        });
      }
      throw e;
    }
  };

  /**
   * Soft-delete a project. Optimistic — removes from the in-memory
   * list under its program and decrements the parent program's
   * projectCount. Rolls back on failure.
   */
  deleteProject = async (programId, projectId) => {
    const list = this.projectsByProgram[programId] || [];
    const idx = list.findIndex((p) => p.id === projectId);
    const prev = idx >= 0 ? list[idx] : null;
    if (idx >= 0) {
      runInAction(() => {
        this.projectsByProgram[programId] = list.filter((p) => p.id !== projectId);
        const programIdx = this.programs.findIndex((p) => p.id === programId);
        if (programIdx >= 0) {
          this.programs[programIdx] = {
            ...this.programs[programIdx],
            projectCount: Math.max(0, (this.programs[programIdx].projectCount || 1) - 1),
          };
        }
      });
    }
    try {
      await deleteProjectQuery(this.authData, projectId, programId);
      return true;
    } catch (e) {
      if (prev) {
        runInAction(() => {
          this.projectsByProgram[programId] = [prev, ...(this.projectsByProgram[programId] || [])];
        });
      }
      throw e;
    }
  };

  shareArtifactUrl = async (artifact) => {
    if (!artifact?.artifactId) {
      throw new Error("artifact.artifactId required for share");
    }
    if (this.shareUrlByArtifact[artifact.artifactId]) {
      return this.shareUrlByArtifact[artifact.artifactId];
    }
    const { url } = await shareArtifact(this.authData, artifact);
    runInAction(() => {
      this.shareUrlByArtifact[artifact.artifactId] = url;
    });
    return url;
  };

  reset = () => {
    this.programs = [];
    this.programsLoading = false;
    this.programsError = null;
    this.projectsByProgram = {};
    this.projectsLoading = {};
    this.projectsError = {};
    this.recentChats = [];
    this.recentChatsLoading = false;
    this.recentChatsError = null;
    this.membersByProject = {};
    this.chatsByProject = {};
    this.pinnedByProject = {};
    this.shareUrlByArtifact = {};
  };
}
